import React, { useEffect, useRef, useState } from 'react';
import axios from '../utils/api';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './StoreSettingsManager.css';
import { COUNTRY_ISD_CODES, getPhoneRuleByIsd } from '../constants/countryIsdCodes';

const initialForm = {
  store_name: '',
  legal_name: '',
  gst_number: '',
  pan_number: '',
  contact_email: '',
  contact_phone_country: '',
  contact_phone: '',
  store_addr1: '',
  store_addr2: '',
  store_city: '',
  store_state: '',
  store_country: '',
  store_pin: '',
  cod_enabled: false,
  razorpay_enabled: true,
  allow_non_india_user_isd: false,
  allow_add_to_cart: true,
  critical_inventory_threshold_percent: '10',
  cart_messages_text: 'Free shipping on orders over $50\n30-day return policy\nSecure checkout',
  default_return_policy: 'non-returnable',
  return_policy: '',
  shipping_policy: '',
  privacy_policy: '',
};

const StoreSettingsManager = () => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testCountryIsd, setTestCountryIsd] = useState('+91');
  const [testPhone, setTestPhone] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState('');
  const [testSuccess, setTestSuccess] = useState('');
  const returnPolicyEditorRef = useRef(null);
  const returnPolicyQuillRef = useRef(null);
  const shippingPolicyEditorRef = useRef(null);
  const shippingPolicyQuillRef = useRef(null);
  const privacyPolicyEditorRef = useRef(null);
  const privacyPolicyQuillRef = useRef(null);

  const testPhoneRule = getPhoneRuleByIsd(testCountryIsd);
  const testLengthHint = testPhoneRule.minLength === testPhoneRule.maxLength
    ? `${testPhoneRule.minLength} digits`
    : `${testPhoneRule.minLength}-${testPhoneRule.maxLength} digits`;

  useEffect(() => {
    fetchStoreSettings();
  }, []);

  useEffect(() => {
    if (!returnPolicyEditorRef.current || returnPolicyQuillRef.current) {
      return;
    }

    const quill = new Quill(returnPolicyEditorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'clean'],
        ],
      },
    });

    quill.on('text-change', () => {
      const html = quill.root.innerHTML;
      setFormData((current) => ({ ...current, return_policy: html }));
    });

    returnPolicyQuillRef.current = quill;
  }, [loading]);

  useEffect(() => {
    if (!privacyPolicyEditorRef.current || privacyPolicyQuillRef.current) {
      return;
    }

    const quill = new Quill(privacyPolicyEditorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'clean'],
        ],
      },
    });

    quill.on('text-change', () => {
      const html = quill.root.innerHTML;
      setFormData((current) => ({ ...current, privacy_policy: html }));
    });

    privacyPolicyQuillRef.current = quill;
  }, [loading]);

  useEffect(() => {
    if (!shippingPolicyEditorRef.current || shippingPolicyQuillRef.current) {
      return;
    }

    const quill = new Quill(shippingPolicyEditorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'clean'],
        ],
      },
    });

    quill.on('text-change', () => {
      const html = quill.root.innerHTML;
      setFormData((current) => ({ ...current, shipping_policy: html }));
    });

    shippingPolicyQuillRef.current = quill;
  }, [loading]);

  useEffect(() => {
    if (!shippingPolicyQuillRef.current) {
      return;
    }

    const currentHtml = shippingPolicyQuillRef.current.root.innerHTML;
    const nextHtml = formData.shipping_policy || '';
    if (currentHtml !== nextHtml) {
      shippingPolicyQuillRef.current.root.innerHTML = nextHtml;
    }
  }, [formData.shipping_policy]);

  useEffect(() => {
    if (!returnPolicyQuillRef.current) {
      return;
    }

    const currentHtml = returnPolicyQuillRef.current.root.innerHTML;
    const nextHtml = formData.return_policy || '';
    if (currentHtml !== nextHtml) {
      returnPolicyQuillRef.current.root.innerHTML = nextHtml;
    }
  }, [formData.return_policy]);

  useEffect(() => {
    if (!privacyPolicyQuillRef.current) {
      return;
    }

    const currentHtml = privacyPolicyQuillRef.current.root.innerHTML;
    const nextHtml = formData.privacy_policy || '';
    if (currentHtml !== nextHtml) {
      privacyPolicyQuillRef.current.root.innerHTML = nextHtml;
    }
  }, [formData.privacy_policy]);

  const fetchStoreSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/store-settings');
      const data = response.data;
      setFormData({
        store_name: data.store_name || '',
        legal_name: data.legal_name || '',
        gst_number: data.gst_number || '',
        pan_number: data.pan_number || '',
        contact_email: data.contact_email || '',
        contact_phone_country: data.contact_phone_country?.toString() || '',
        contact_phone: data.contact_phone || '',
        store_addr1: data.store_addr1 || '',
        store_addr2: data.store_addr2 || '',
        store_city: data.store_city || '',
        store_state: data.store_state || '',
        store_country: data.store_country || '',
        store_pin: data.store_pin?.toString() || '',
        cod_enabled: Boolean(data.cod_enabled),
        razorpay_enabled: data.razorpay_enabled !== false,
        allow_non_india_user_isd: Boolean(data.allow_non_india_user_isd),
        allow_add_to_cart: data.allow_add_to_cart !== false,
        critical_inventory_threshold_percent: (data.critical_inventory_threshold_percent ?? 10).toString(),
        cart_messages_text: Array.isArray(data.cart_messages)
          ? data.cart_messages.join('\n')
          : initialForm.cart_messages_text,
        default_return_policy: data.default_return_policy || 'non-returnable',
        return_policy: data.return_policy || '',
        shipping_policy: data.shipping_policy || '',
        privacy_policy: data.privacy_policy || '',
      });
    } catch (err) {
      if (err.response?.status !== 404) {
        setError('Failed to fetch store settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cod_enabled' || name === 'razorpay_enabled' || name === 'allow_non_india_user_isd' || name === 'allow_add_to_cart') {
      setFormData({ ...formData, [name]: e.target.checked });
      return;
    }

    if (name === 'contact_phone_country') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 3) });
      return;
    }

    if (name === 'contact_phone') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 10) });
      return;
    }

    if (name === 'gst_number') {
      const normalized = value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15);
      setFormData({ ...formData, [name]: normalized });
      return;
    }

    if (name === 'pan_number') {
      const normalized = value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 10);
      setFormData({ ...formData, [name]: normalized });
      return;
    }

    if (name === 'store_pin') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 6) });
      return;
    }

    if (name === 'critical_inventory_threshold_percent') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 3) });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (formData.store_name.length > 40) return 'Store name cannot exceed 40 characters';
    if (formData.legal_name.length > 80) return 'Legal name cannot exceed 80 characters';
    if (formData.gst_number && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gst_number)) {
      return 'GST number format is invalid';
    }
    if (formData.pan_number && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(formData.pan_number)) {
      return 'PAN number format is invalid';
    }
    if (!/^\d{1,3}$/.test(formData.contact_phone_country)) return 'Country code must be 1 to 3 digits';
    if (!/^\d{10}$/.test(formData.contact_phone)) return 'Contact phone must be exactly 10 digits';
    if (formData.store_addr1.length > 100) return 'Address line 1 cannot exceed 100 characters';
    if (formData.store_addr2.length > 100) return 'Address line 2 cannot exceed 100 characters';
    if (formData.store_city.length > 40) return 'City cannot exceed 40 characters';
    if (formData.store_state.length > 40) return 'State cannot exceed 40 characters';
    if (formData.store_country.length > 40) return 'Country cannot exceed 40 characters';
    if (!/^\d{6}$/.test(formData.store_pin)) return 'PIN must be exactly 6 digits';
    if (!/^\d{1,3}$/.test(formData.critical_inventory_threshold_percent)) return 'Critical inventory threshold must be 0 to 100';
    const thresholdValue = Number(formData.critical_inventory_threshold_percent);
    if (Number.isNaN(thresholdValue) || thresholdValue < 0 || thresholdValue > 100) {
      return 'Critical inventory threshold must be between 0 and 100';
    }

    const cartMessages = formData.cart_messages_text
      .split('\n')
      .map((message) => message.trim())
      .filter(Boolean);

    if (cartMessages.length > 20) return 'You can configure up to 20 cart messages';
    if (cartMessages.some((message) => message.length > 200)) return 'Each cart message must be at most 200 characters';

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        legal_name: formData.legal_name.trim() || null,
        gst_number: formData.gst_number.trim() || null,
        pan_number: formData.pan_number.trim() || null,
        contact_phone_country: Number(formData.contact_phone_country),
        store_pin: Number(formData.store_pin),
        critical_inventory_threshold_percent: Number(formData.critical_inventory_threshold_percent),
        cart_messages: formData.cart_messages_text
          .split('\n')
          .map((message) => message.trim())
          .filter(Boolean),
        default_return_policy: formData.default_return_policy || 'non-returnable',
        return_policy: formData.return_policy || '',
        shipping_policy: formData.shipping_policy || '',
        privacy_policy: formData.privacy_policy || '',
      };
      delete payload.cart_messages_text;
      await axios.put('/api/store-settings', payload);
      setSuccess('Store settings saved successfully');
    } catch (err) {
      let errorMsg = 'Failed to save store settings';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg = err.response.data.detail
            .map((detail) => `${detail.loc[detail.loc.length - 1]}: ${detail.msg}`)
            .join(', ');
        } else {
          errorMsg = err.response.data.detail;
        }
      }
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleTestValidation = async (e) => {
    e.preventDefault();
    setTestError('');
    setTestSuccess('');

    const cleanPhone = testPhone.replace(/\D/g, '');
    if (!cleanPhone) {
      setTestError('Enter a mobile number to test');
      return;
    }

    try {
      setTestLoading(true);
      const response = await axios.post('/api/auth/validate-phone', {
        country_isd_code: testCountryIsd,
        phone_number: cleanPhone,
      });
      setTestSuccess(response.data?.message || 'Validation passed');
    } catch (err) {
      setTestError(err.response?.data?.detail || 'Validation failed');
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="store-settings-container">
        <p>Loading store settings...</p>
      </div>
    );
  }

  return (
    <div className="store-settings-container">
      <div className="store-settings-card">
        <h1>Store Settings</h1>
        <p className="subtext">Administrator / Super User configuration for storefront contact and cart messaging details.</p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit} className="store-settings-form" noValidate>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Store Name *</label>
              <input
                type="text"
                name="store_name"
                value={formData.store_name}
                onChange={handleChange}
                maxLength={40}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Legal Name</label>
              <input
                type="text"
                name="legal_name"
                value={formData.legal_name}
                onChange={handleChange}
                maxLength={80}
                placeholder="Registered legal entity name"
              />
            </div>

            <div className="form-group">
              <label>GST Number</label>
              <input
                type="text"
                name="gst_number"
                value={formData.gst_number}
                onChange={handleChange}
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
              />
            </div>

            <div className="form-group">
              <label>PAN Number</label>
              <input
                type="text"
                name="pan_number"
                value={formData.pan_number}
                onChange={handleChange}
                maxLength={10}
                placeholder="AAAAA0000A"
              />
            </div>

            <div className="form-group">
              <label>Contact Email *</label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Country Code (1-3 digits) *</label>
              <input
                type="text"
                name="contact_phone_country"
                value={formData.contact_phone_country}
                onChange={handleChange}
                inputMode="numeric"
                required
              />
            </div>

            <div className="form-group">
              <label>Contact Phone (10 digits) *</label>
              <input
                type="text"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleChange}
                inputMode="numeric"
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Address Line 1 *</label>
              <input
                type="text"
                name="store_addr1"
                value={formData.store_addr1}
                onChange={handleChange}
                maxLength={100}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Address Line 2</label>
              <input
                type="text"
                name="store_addr2"
                value={formData.store_addr2}
                onChange={handleChange}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="store_city"
                value={formData.store_city}
                onChange={handleChange}
                maxLength={40}
                required
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                name="store_state"
                value={formData.store_state}
                onChange={handleChange}
                maxLength={40}
                required
              />
            </div>

            <div className="form-group">
              <label>Country *</label>
              <input
                type="text"
                name="store_country"
                value={formData.store_country}
                onChange={handleChange}
                maxLength={40}
                required
              />
            </div>

            <div className="form-group">
              <label>PIN (6 digits) *</label>
              <input
                type="text"
                name="store_pin"
                value={formData.store_pin}
                onChange={handleChange}
                inputMode="numeric"
                required
              />
            </div>

            <div className="form-group">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  name="cod_enabled"
                  checked={formData.cod_enabled}
                  onChange={handleChange}
                />
                Enable Cash on Delivery (COD)
              </label>
            </div>

            <div className="form-group">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  name="razorpay_enabled"
                  checked={formData.razorpay_enabled}
                  onChange={handleChange}
                />
                Enable Razorpay Payments
              </label>
            </div>

            <div className="form-group full-width">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  name="allow_add_to_cart"
                  checked={formData.allow_add_to_cart}
                  onChange={handleChange}
                />
                Allow Add to Cart (enable buyers to add products to cart and purchase)
              </label>
              <small className="text-muted">When unchecked, the Add to Cart button will be disabled for all buyers.</small>
            </div>

            <div className="form-group full-width">
              <label className="d-flex align-items-center gap-2">
                <input
                  type="checkbox"
                  name="allow_non_india_user_isd"
                  checked={formData.allow_non_india_user_isd}
                  onChange={handleChange}
                />
                Allow non-India country ISD code selection on user registration
              </label>
            </div>

            <div className="form-group">
              <label>Critical Inventory Threshold (%)</label>
              <input
                type="text"
                name="critical_inventory_threshold_percent"
                value={formData.critical_inventory_threshold_percent}
                onChange={handleChange}
                inputMode="numeric"
                placeholder="10"
              />
              <small className="text-muted">Used to flag SKUs as critical in analytics dashboard.</small>
            </div>

            <div className="form-group">
              <label>Default Return Policy</label>
              <select
                name="default_return_policy"
                value={formData.default_return_policy}
                onChange={handleChange}
              >
                <option value="non-returnable">Non-Returnable</option>
                <option value="5-days">5 Days</option>
                <option value="10-days">10 Days</option>
                <option value="15-days">15 Days</option>
                <option value="30-days">30 Days</option>
              </select>
              <small className="text-muted">Applies to all products unless overridden at the product level.</small>
            </div>

            <div className="form-group full-width">
              <label>Messages Below Add to Cart (one per line)</label>
              <textarea
                name="cart_messages_text"
                value={formData.cart_messages_text}
                onChange={handleChange}
                rows={5}
                placeholder="Enter one message per line"
              />
              <small className="text-muted">These messages are shown as bullet points below the Add to Cart button.</small>
            </div>

            <div className="form-group full-width">
              <label>Return Policy Content (HTML)</label>
              <div className="store-settings-rte" ref={returnPolicyEditorRef} />
              <small className="text-muted">This content will appear from the Footer Return Policy link.</small>
            </div>

            <div className="form-group full-width">
              <label>Shipping Policy Content (HTML)</label>
              <div className="store-settings-rte" ref={shippingPolicyEditorRef} />
              <small className="text-muted">This content will appear from the Footer Shipping Policy link.</small>
            </div>

            <div className="form-group full-width">
              <label>Privacy and Data Protection Policy Content (HTML)</label>
              <div className="store-settings-rte" ref={privacyPolicyEditorRef} />
              <small className="text-muted">This content will appear from the Footer Privacy and Data Protection Policy link.</small>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Store Settings'}
            </button>
          </div>
        </form>

        <hr style={{ margin: '2rem 0' }} />

        <h2 style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>Admin Phone Validation Test</h2>
        <p className="subtext" style={{ marginBottom: '1rem' }}>
          Quick pre-release check for ISD + mobile number combinations using backend validation.
        </p>

        {testError && <div className="alert alert-error">{testError}</div>}
        {testSuccess && <div className="alert alert-success">{testSuccess}</div>}

        <form onSubmit={handleTestValidation} className="store-settings-form" noValidate>
          <div className="form-grid">
            <div className="form-group">
              <label>Country ISD Code</label>
              <select
                value={testCountryIsd}
                onChange={(e) => {
                  setTestCountryIsd(e.target.value);
                  setTestPhone('');
                  setTestError('');
                  setTestSuccess('');
                }}
              >
                {COUNTRY_ISD_CODES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Mobile Number to Validate</label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, '').slice(0, testPhoneRule.maxLength))}
                inputMode="numeric"
                placeholder={`Expected: ${testLengthHint}`}
              />
              <small className="text-muted">Expected length for {testCountryIsd}: {testLengthHint}</small>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={testLoading}>
              {testLoading ? 'Testing...' : 'Test Backend Validation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreSettingsManager;
